import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyPayment } from "../../../slice/checkout/checkoutSlice";
import Footer from "../../users/components/Footer";
import Header from "../Header";
import {
  removeCartItem,
  fetchCart,
  updateCartQuantity,
} from "../../../slice/cart/cartSlice";
import { fetchAddresses } from "../../../slice/address/addressSlice";
import { fetchCoupons } from "../../../slice/coupons/couponSlice";

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
const [isPaying, setIsPaying] = useState(false);
  const { items: cartItems } = useSelector((state) => state.cart);
  const { token } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.checkout);
  const { dropdownCategories = [], loading: categoryLoading } = useSelector((state) => state.category);
  const { addresses = [] } = useSelector((state) => state.address);
  const { list: couponsList, loading: couponsLoading } = useSelector((state) => state.coupon);

  const [currentStep, setCurrentStep] = useState(1);
  const [couponCode, setCouponCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);


useEffect(() => {
  window.scrollTo({ top: 0, behavior: "smooth" });
}, [currentStep]);


useEffect(() => {
  window.scrollTo({ top: 0, behavior: "smooth" });
}, []);
  const [billingInfo, setBillingInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    district: "",
    state: "",
    pincode: "",
    notes: "",
  });

  // ─── Styles ───────────────────────────────────────────────────────────────
  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: "10px",
    border: "1.5px solid #e5e7eb",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const inputErrorStyle = {
    ...inputStyle,
    border: "1.5px solid #ef4444",
    background: "#fff5f5",
  };

  const labelStyle = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "6px",
    display: "block",
  };

  const errorTextStyle = {
    color: "#ef4444",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
  };

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (token) {
      dispatch(fetchCart());
      dispatch(fetchAddresses());
      dispatch(fetchCoupons({ page: 1, limit: 100 }));
    }
  }, [token, dispatch]);

  // Filter available coupons (only active ones)
  useEffect(() => {
    if (couponsList && couponsList.length > 0) {
      // Filter only active coupons (status === true)
      const validCoupons = couponsList.filter(coupon => coupon.status === true);
      setAvailableCoupons(validCoupons);
    }
  }, [couponsList]);

  // ─── Calculations ─────────────────────────────────────────────────────────
// In your Checkout component, update the calculations section:

// ─── Calculations ─────────────────────────────────────────────────────────
const subtotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);


const shipping = 0;
const total = subtotal - appliedDiscount + shipping;

// Optional: Add debug logging to see what's happening
useEffect(() => {
  console.log('Cart Items:', cartItems);
  console.log('Subtotal calculated:', subtotal);
  cartItems.forEach(item => {
    console.log(`Item: ${item.product?.name}, Price from product: ${item.product?.sellingPrice}, Price from cart: ${item.price}, Quantity: ${item.quantity}`);
  });
}, [cartItems, subtotal]);
  

  // ─── Validation ───────────────────────────────────────────────────────────
  const validateBillingInfo = () => {
    const newErrors = {};

    if (!billingInfo.fullName.trim())
      newErrors.fullName = "Full name is required";

    if (!billingInfo.phone.trim())
      newErrors.phone = "Phone number is required";
    else if (!/^[6-9]\d{9}$/.test(billingInfo.phone))
      newErrors.phone = "Invalid Indian mobile number";

    if (!billingInfo.address.trim())
      newErrors.address = "Address is required";

    if (!billingInfo.district.trim())
      newErrors.district = "district is required";

    if (!billingInfo.state.trim())
      newErrors.state = "State is required";

    if (!billingInfo.pincode.trim())
      newErrors.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(billingInfo.pincode))
      newErrors.pincode = "Invalid pincode (must be 6 digits)";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Coupon Functions ─────────────────────────────────────────────────────
  const applyCouponByCode = async (code) => {
    const coupon = couponsList.find(c => c.code === code.toUpperCase());
    
    if (!coupon) {
      toast.error("Please apply the coupon code");
      return false;
    }
    
    return applyCoupon(coupon);
  };

  const applyCoupon = (coupon) => {
    // Check if coupon is active
    if (coupon.status !== true) {
      toast.error("This coupon is no longer active");
      return false;
    }
    
    // Calculate discount (fixed amount from your API)
    const discount = Math.min(coupon.discountAmount, subtotal);
    
    if (discount > 0) {
      setAppliedDiscount(discount);
      setAppliedCoupon(coupon);
      setCouponCode(coupon.code);
      toast.success(`Coupon applied! You saved ₹${discount.toLocaleString()}`);
      return true;
    }
    
    toast.error("Could not apply coupon");
    return false;
  };

  const removeCoupon = () => {
    setAppliedDiscount(0);
    setAppliedCoupon(null);
    setCouponCode("");
    toast.info("Coupon removed");
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setBillingInfo((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectSavedAddress = (addr) => {
    setSelectedSavedAddress(addr._id);
    setBillingInfo({
      fullName: addr.fullName || addr.name || "",
      email: addr.email || "",
      phone: addr.phone || "",
      address: addr.address || addr.street || "",
      district: addr.district || "",
      state: addr.state || "",
      pincode: addr.pincode || addr.zipCode || "",
      notes: "",
    });
  };

  const handleNextStep = () => {
    if (currentStep === 2) {
      if (showNewForm || addresses.length === 0) {
        const isValid = validateBillingInfo();
        if (!isValid) {
          toast.error("Please fill all required address details");
          return;
        }
      } else {
        if (!selectedSavedAddress) {
          toast.error("Please select a delivery address");
          return;
        }
      }
    }
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleIncreaseQty = (item) => {
    dispatch(updateCartQuantity({ id: item._id, quantity: item.quantity + 1 }));
  };

  const handleDecreaseQty = (item) => {
    if (item.quantity === 1) {
      dispatch(removeCartItem(item._id)).then(() => dispatch(fetchCart()));
      return;
    }
    dispatch(updateCartQuantity({ id: item._id, quantity: item.quantity - 1 }));
  };

  const createRazorpayOrder = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/checkout/create-razorpay-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount: total, currency: "INR" }),
      }
    );
    if (!response.ok) throw new Error("Failed to create order");
    const data = await response.json();
    return data.order;
  };

const handlePlaceOrder = async () => {
  if (
    !billingInfo.fullName ||
    !billingInfo.phone ||
    !billingInfo.address
  ) {
    toast.error("Please fill in all required fields");
    return;
  }

  if (!window.Razorpay) {
    toast.error("Razorpay SDK not loaded.");
    return;
  }

  if (cartItems.length === 0) {
    toast.error("Your cart is empty");
    return;
  }

  setIsPaying(true); // disable button

  try {
    const order = await createRazorpayOrder();

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      order_id: order.id,
      name: "Shaart",
      description: "Order Payment",
      image: "/logo.png",

      handler: function (response) {
        dispatch(
          verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            shippingAddress: billingInfo,
            cartItems,
            totalAmount: total,
            appliedCoupon: appliedCoupon,
            discountAmount: appliedDiscount,
          })
        )
          .unwrap()
          .then(() => {
            toast.success("Order placed successfully!");
            navigate("/");
          })
          .catch((err) => {
            setIsPaying(false);
            toast.error(err?.message || "Payment verification failed");
          });
      },

      modal: {
        ondismiss: () => {
          setIsPaying(false);
          toast.info("Payment cancelled");
        },
      },

      theme: { color: "#f97316" },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", (response) => {
      setIsPaying(false);
      toast.error(`Payment failed: ${response.error.description}`);
    });

    rzp.open();

  } catch (error) {
    setIsPaying(false);
    toast.error("Failed to process payment. Please try again.");
  }
};

  const steps = ["Cart Review", "Shipping Address", "Payment"];

  // Coupon Modal Component
  const CouponModal = () => {
    if (!showCouponModal) return null;
    
    // Log to see what coupons are available
    console.log("Available Coupons:", availableCoupons);
    console.log("All Coupons from Redux:", couponsList);
   
    

    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }} onClick={() => setShowCouponModal(false)}>
        <div style={{
          backgroundColor: "#fff",
          borderRadius: "20px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
          padding: "24px",
          position: "relative",
        }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowCouponModal(false)}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#9ca3af",
            }}
          >
            ✕
          </button>
          
          <h3 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "20px", color: "#1f2937" }}>
            Available Coupons 🎁
          </h3>
          
          {couponsLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>Loading coupons...</div>
          ) : availableCoupons.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>🏷️</div>
              No coupons available at the moment
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {availableCoupons.map((coupon) => (
                <div
                  key={coupon._id}
                  style={{
                    padding: "16px",
                    borderRadius: "12px",
                    border: appliedCoupon?._id === coupon._id ? "2px solid #f97316" : "1.5px solid #e5e7eb",
                    background: appliedCoupon?._id === coupon._id ? "#fff8f0" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    applyCoupon(coupon);
                    setShowCouponModal(false);
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <div>
                      <span style={{
                        background: "#f97316",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "1px",
                      }}>
                        {coupon.code}
                      </span>
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: 700, color: "#f97316" }}>
                      ₹{coupon.discountAmount.toLocaleString()} OFF
                    </div>
                  </div>
                  
                  <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
                    Get ₹{coupon.discountAmount.toLocaleString()} discount on your order
                  </p>
                  
                  <div style={{ fontSize: "11px", color: "#9ca3af", display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "8px" }}>
                    <span>✓ Valid on all orders</span>
                    <span>📅 Added on: {new Date(coupon.createdAt).toLocaleDateString()}</span>
                  </div>
                  
                  {appliedCoupon?._id === coupon._id && (
                    <div style={{ marginTop: "10px", color: "#16a34a", fontSize: "12px", fontWeight: 600 }}>
                      ✓ Applied
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#fdf6ee", minHeight: "100vh" }}>
      <Header
        dropdownCategories={dropdownCategories}
        loading={categoryLoading}
      />

      <CouponModal />

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 16px 60px" }}>
        {/* Page Title */}
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: 700, color: "#1f2937", margin: 0 }}>
            Secure Checkout
          </h1>
          <p className="margintop" style={{ color: "#9ca3af", marginTop: "6px", fontSize: "14px" }}>
            Complete your purchase in just a few steps
          </p>
        </div>

        <button
          onClick={() => navigate("/")}
          style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            background: "none", border: "none", cursor: "pointer",
            color: "#f97316", fontSize: "14px", fontWeight: 600, padding: "6px 0",
          }}
        >
          ← Go Back / Home
        </button>

        {/* Steps */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "40px", gap: "0" }}>
          {steps.map((label, i) => {
            const stepNum = i + 1;
            const isActive = currentStep === stepNum;
            const isCompleted = currentStep > stepNum;
            return (
              <React.Fragment key={stepNum}>
                <div
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", cursor: "pointer" }}
                  onClick={() => stepNum < currentStep && setCurrentStep(stepNum)}
                >
                  <div style={{
                    width: "42px", height: "42px", borderRadius: "50%",
                    background: isCompleted ? "#f97316" : isActive ? "#f97316" : "#fff",
                    border: `2px solid ${isCompleted || isActive ? "#f97316" : "#e5e7eb"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: "15px",
                    color: isCompleted || isActive ? "#fff" : "#9ca3af",
                    boxShadow: isActive ? "0 0 0 4px rgba(249,115,22,0.15)" : "none",
                    transition: "all 0.3s",
                  }}>
                    {isCompleted ? "✓" : stepNum}
                  </div>
                  <span style={{
                    fontSize: "12px", fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#f97316" : isCompleted ? "#374151" : "#9ca3af",
                    whiteSpace: "nowrap",
                  }}>
                    {label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div style={{
                    height: "2px", width: "80px", marginBottom: "20px",
                    background: currentStep > i + 1 ? "#f97316" : "#e5e7eb",
                    transition: "background 0.3s",
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Content */}
        <div style={{ display: "flex", gap: "24px", alignItems: "flex-start", flexWrap: "wrap" }}>

          {/* ── Main Panel ── */}
          <div style={{
            flex: "1 1 560px", background: "#fff", borderRadius: "20px",
            padding: "32px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          }}>

            {/* ── STEP 1: Cart Review ── */}
            {currentStep === 1 && (
              <div>
                <h2 style={{ fontSize: "20px", fontWeight: 700, color: "#1f2937", marginBottom: "24px", display: "flex", alignItems: "center", gap: "10px" }}>
                  🛒 Review Your Cart
                </h2>
                {cartItems.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af" }}>
                    <div style={{ fontSize: "48px", marginBottom: "12px" }}>🛒</div>
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "32px" }}>
                      {cartItems.map((item) => (
                        <div key={item._id} className="cartBOX" style={{
                          display: "flex", alignItems: "center", gap: "16px",
                          padding: "16px", borderRadius: "14px",
                          border: "1.5px solid #f3f4f6", background: "#fafafa",
                        }}>
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <img
                              src={`${process.env.REACT_APP_API_URL}/uploads/${item.images[0]}`}
                              alt={item.product?.name}
                              style={{ width: "72px", height: "72px", borderRadius: "10px", objectFit: "cover" }}
                            />
                            <span style={{
                              position: "absolute", top: "-6px", right: "-6px",
                              background: "#f97316", color: "#fff", borderRadius: "50%",
                              width: "20px", height: "20px", fontSize: "11px",
                              display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
                            }}>{item.quantity}</span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: "15px", color: "#1f2937", marginBottom: "6px" }}>
                              {item.product?.name}
                            </div>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "12px", background: "#fef3c7", color: "#92400e", padding: "2px 10px", borderRadius: "20px" }}>
                                📏 {item.frameSize}
                              </span>
                            <span
  style={{
    fontSize: "12px",
    background: "#fee2e2",
    color: "#991b1b",
    padding: "2px 10px",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  }}
>
  🎨
  <span
    style={{
      width: "14px",
      height: "14px",
      borderRadius: "50%",
      backgroundColor: item.productColor,
      border: "1px solid #ccc",
      display: "inline-block",
    }}
  ></span>
  {item.frameColor}
</span>
                            </div>
                          </div>
                          <div className="cartBOX" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "10px" }}>
                            <span style={{ fontWeight: 700, fontSize: "16px", color: "#f97316" }}>
                              ₹{item.price}
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <button
                                onClick={() => handleDecreaseQty(item)}
                                style={{
                                  width: "28px", height: "28px", borderRadius: "50%",
                                  border: "1.5px solid #e5e7eb", background: "#fff",
                                  cursor: "pointer", fontSize: "14px",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}
                              >−</button>
                              <span style={{ fontWeight: 600, minWidth: "20px", textAlign: "center" }}>{item.quantity}</span>
                              <button
                                onClick={() => handleIncreaseQty(item)}
                                style={{
                                  width: "28px", height: "28px", borderRadius: "50%",
                                  border: "1.5px solid #e5e7eb", background: "#fff",
                                  cursor: "pointer", fontSize: "14px",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}
                              >+</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={handleNextStep}
                      style={{
                        width: "100%", padding: "14px", borderRadius: "12px",
                        background: "linear-gradient(135deg, #f97316, #ea580c)",
                        color: "#fff", border: "none", cursor: "pointer",
                        fontSize: "15px", fontWeight: 700, letterSpacing: "0.3px",
                      }}
                    >
                      Continue to Shipping →
                    </button>
                  </>
                )}
              </div>
            )}

            {/* ── STEP 2: Shipping Address ── */}
            {currentStep === 2 && (
              <div>
                <h2 style={{ fontSize: "20px", fontWeight: 700, color: "#1f2937", marginBottom: "24px", display: "flex", alignItems: "center", gap: "10px" }}>
                  📍 Shipping Address
                </h2>

                {/* Saved Addresses list — hidden when showNewForm */}
                {addresses.length > 0 && !showNewForm && (
                  <div style={{ marginBottom: "24px" }}>
                    <p style={{ fontSize: "13px", fontWeight: 600, color: "#6b7280", marginBottom: "12px" }}>
                      SELECT A SAVED ADDRESS
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {addresses.map((addr) => (
                        <div
                          key={addr._id}
                          onClick={() => handleSelectSavedAddress(addr)}
                          style={{
                            padding: "16px", borderRadius: "14px", cursor: "pointer",
                            border: `2px solid ${selectedSavedAddress === addr._id ? "#f97316" : "#e5e7eb"}`,
                            background: selectedSavedAddress === addr._id ? "#fff8f0" : "#fafafa",
                            transition: "all 0.2s", position: "relative",
                          }}
                        >
                          {addr.isDefault && (
                            <span style={{
                              position: "absolute", top: "12px", right: "12px",
                              background: "#fef3c7", color: "#92400e",
                              fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "20px",
                            }}>⭐ Default</span>
                          )}
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                            <div style={{
                              width: "20px", height: "20px", borderRadius: "50%",
                              marginTop: "2px", flexShrink: 0,
                              border: `2px solid ${selectedSavedAddress === addr._id ? "#f97316" : "#d1d5db"}`,
                              background: selectedSavedAddress === addr._id ? "#f97316" : "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              {selectedSavedAddress === addr._id && (
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#fff" }} />
                              )}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: "14px", color: "#1f2937" }}>
                                {addr.fullName || addr.name}
                              </div>
                              <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px", lineHeight: "1.5" }}>
                                {addr.address || addr.street}, {addr.district}, {addr.state} – {addr.pincode || addr.zipCode}
                              </div>
                              <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
                                {addr.email} · {addr.phone}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => {
                        setShowNewForm(true);
                        setSelectedSavedAddress(null);
                        setBillingInfo({ fullName: "", email: "", phone: "", address: "", district: "", state: "", pincode: "", notes: "" });
                        setErrors({});
                      }}
                      style={{
                        marginTop: "14px", padding: "10px 18px", borderRadius: "10px",
                        border: "1.5px dashed #f97316", background: "transparent",
                        color: "#f97316", fontSize: "13px", fontWeight: 700,
                        cursor: "pointer", width: "100%",
                      }}
                    >
                      + Use a Different Address
                    </button>
                  </div>
                )}

                {/* New Address Form */}
                {(showNewForm || addresses.length === 0) && (
                  <div>
                    {addresses.length > 0 && (
                      <button
                        onClick={() => { setShowNewForm(false); setErrors({}); }}
                        style={{
                          marginBottom: "16px", background: "none", border: "none",
                          color: "#f97316", cursor: "pointer", fontSize: "13px",
                          fontWeight: 600, padding: 0,
                        }}
                      >
                        ← Back to saved addresses
                      </button>
                    )}

                    {/* Full Name */}
                    <div style={{ marginBottom: "16px" }}>
                      <label style={labelStyle}>
                        Full Name <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        name="fullName"
                        value={billingInfo.fullName}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        style={errors.fullName ? inputErrorStyle : inputStyle}
                      />
                      {errors.fullName && <span style={errorTextStyle}>⚠ {errors.fullName}</span>}
                    </div>

                    {/* Email + Phone */}
                    <div style={{ display: "flex", gap: "14px", marginBottom: "16px", flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 200px" }}>
                        <label style={labelStyle}>Email Address (Optional)</label>
                        <input
                          name="email"
                          value={billingInfo.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          style={errors.email ? inputErrorStyle : inputStyle}
                        />
                        {errors.email && <span style={errorTextStyle}>⚠ {errors.email}</span>}
                      </div>
                      <div style={{ flex: "1 1 200px" }}>
                        <label style={labelStyle}>
                          Phone Number <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          name="phone"
                          value={billingInfo.phone}
                          onChange={handleChange}
                          placeholder="10-digit mobile number"
                          style={errors.phone ? inputErrorStyle : inputStyle}
                        />
                        {errors.phone && <span style={errorTextStyle}>⚠ {errors.phone}</span>}
                      </div>
                    </div>

                    {/* Street Address */}
                    <div style={{ marginBottom: "16px" }}>
                      <label style={labelStyle}>
                        Street Address <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <textarea
                        name="address"
                        value={billingInfo.address}
                        onChange={handleChange}
                        placeholder="House no, Street, Area"
                        rows={3}
                        style={errors.address ? { ...inputErrorStyle, resize: "vertical" } : { ...inputStyle, resize: "vertical" }}
                      />
                      {errors.address && <span style={errorTextStyle}>⚠ {errors.address}</span>}
                    </div>

                    {/* City + State */}
                    <div style={{ display: "flex", gap: "14px", marginBottom: "16px", flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 200px" }}>
                        <label style={labelStyle}>
                          District <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          name="district"
                          value={billingInfo.district}
                          onChange={handleChange}
                          placeholder="District"
                          style={errors.district ? inputErrorStyle : inputStyle}
                        />
                        {errors.district && <span style={errorTextStyle}>⚠ {errors.district}</span>}
                      </div>
                      <div style={{ flex: "1 1 200px" }}>
                        <label style={labelStyle}>
                          State <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          name="state"
                          value={billingInfo.state}
                          onChange={handleChange}
                          placeholder="State"
                          style={errors.state ? inputErrorStyle : inputStyle}
                        />
                        {errors.state && <span style={errorTextStyle}>⚠ {errors.state}</span>}
                      </div>
                    </div>

                    {/* Pincode */}
                    <div style={{ marginBottom: "16px" }}>
                      <label style={labelStyle}>
                        PIN Code <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        name="pincode"
                        value={billingInfo.pincode}
                        onChange={handleChange}
                        placeholder="6-digit PIN code"
                        maxLength={6}
                        style={errors.pincode ? inputErrorStyle : inputStyle}
                      />
                      {errors.pincode && <span style={errorTextStyle}>⚠ {errors.pincode}</span>}
                    </div>

                    {/* Order Notes */}
                    <div style={{ marginBottom: "8px" }}>
                      <label style={labelStyle}>Order Notes (Optional)</label>
                      <textarea
                        name="notes"
                        value={billingInfo.notes}
                        onChange={handleChange}
                        placeholder="Any special instructions..."
                        rows={3}
                        style={{ ...inputStyle, resize: "vertical" }}
                      />
                    </div>
                  </div>
                )}

                {/* Bottom Buttons */}
                <div style={{ display: "flex", gap: "12px", marginTop: "28px" }}>
                  <button
                  className="continuePayent"
                    onClick={handlePrevStep}
                    style={{
                      flex: 1, padding: "13px", borderRadius: "12px",
                      border: "1.5px solid #e5e7eb", background: "#fff",
                      cursor: "pointer", fontSize: "14px", fontWeight: 600, color: "#374151",
                    }}
                  >
                    ← Back
                  </button>
                  <button
                  className="continuePayent"
                    onClick={handleNextStep}
                    style={{
                      flex: 2, padding: "13px", borderRadius: "12px",
                      background: "linear-gradient(135deg, #f97316, #ea580c)",
                      color: "#fff", border: "none", cursor: "pointer",
                      fontSize: "15px", fontWeight: 700,
                    }}
                  >
                    Continue to Payment →
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Payment ── */}
            {currentStep === 3 && (
              <div>
                <h2 style={{ fontSize: "20px", fontWeight: 700, color: "#1f2937", marginBottom: "24px", display: "flex", alignItems: "center", gap: "10px" }}>
                  💳 Payment Method
                </h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginBottom: "28px" }}>
                  {[
                    { icon: "🔒", title: "Secure Payment", desc: "Your payment info is encrypted and secure" },
                    { icon: "⚡", title: "Quick Checkout", desc: "Complete your purchase with Razorpay" },
                  ].map((card) => (
                    <div key={card.title} style={{
                      display: "flex", alignItems: "center", gap: "16px",
                      padding: "16px", borderRadius: "14px",
                      background: "#fff8f0", border: "1.5px solid #fed7aa",
                    }}>
                      <span style={{ fontSize: "28px" }}>{card.icon}</span>
                      <div>
                        <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#1f2937" }}>{card.title}</h4>
                        <p style={{ margin: 0, fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>{card.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{
                  padding: "18px", borderRadius: "14px",
                  border: "2px solid #f97316", background: "#fff8f0",
                  display: "flex", alignItems: "center", gap: "16px", marginBottom: "28px",
                }}>
                  <input type="radio" checked readOnly style={{ accentColor: "#f97316", width: "18px", height: "18px" }} />
                  <span style={{ fontSize: "24px" }}>💳</span>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1f2937" }}>Razorpay</h4>
                    <p style={{ margin: 0, fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                      Credit/Debit Card, UPI, Net Banking, Wallets
                    </p>
                  </div>
                </div>

                <button
                  onClick={handlePrevStep}
                  style={{
                    padding: "12px 24px", borderRadius: "12px",
                    border: "1.5px solid #e5e7eb", background: "#fff",
                    cursor: "pointer", fontSize: "14px", fontWeight: 600, color: "#374151",
                  }}
                >
                  ← Back to Address
                </button>
              </div>
            )}
          </div>

          {/* ── Order Summary Sidebar ── */}
          <div className="flex100" style={{ flex: "0 0 320px", display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ background: "#fff", borderRadius: "20px", padding: "24px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
              <h3 style={{
                fontSize: "17px", fontWeight: 700, color: "#1f2937",
                marginBottom: "20px", paddingBottom: "14px", borderBottom: "1.5px solid #f3f4f6",
              }}>
                Order Summary
              </h3>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
                {cartItems.map((item) => (
                  <div key={item._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>{item.product?.name}</div>
                      <div style={{ fontSize: "12px", color: "#9ca3af" }}>Qty: {item.quantity}</div>
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: "#f97316" }}>
                    ₹{item.price * item.quantity}
                    </span>
                  </div>
                ))}
              </div>

              {/* Coupon Section */}
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", display: "block", margin: 0 }}>
                    APPLY COUPON
                  </label>
                  <button
                    onClick={() => setShowCouponModal(true)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#f97316",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    View All Coupons 🎁
                  </button>
                </div>
                
                {appliedCoupon ? (
                  <div style={{
                    padding: "12px",
                    borderRadius: "10px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: "13px", color: "#16a34a" }}>
                        {appliedCoupon.code}
                      </span>
                      <span style={{ fontSize: "11px", color: "#6b7280", marginLeft: "8px" }}>
                        ₹{appliedCoupon.discountAmount} OFF
                      </span>
                    </div>
                    <button
                      onClick={removeCoupon}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#ef4444",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      style={{
                        flex: 1, padding: "9px 12px", borderRadius: "8px",
                        border: "1.5px solid #e5e7eb", fontSize: "13px", outline: "none",
                      }}
                    />
                    <button
                      onClick={() => applyCouponByCode(couponCode)}
                      style={{
                        padding: "9px 14px", borderRadius: "8px",
                        background: "#f97316", color: "#fff",
                        border: "none", cursor: "pointer", fontSize: "13px", fontWeight: 600,
                      }}
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div style={{ borderTop: "1.5px solid #f3f4f6", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", color: "#6b7280" }}>
                  <span>Subtotal</span>
                  <span style={{ fontWeight: 600, color: "#374151" }}>₹{subtotal.toLocaleString()}</span>
                </div>
                {appliedDiscount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                    <span style={{ color: "#6b7280" }}>Discount ({appliedCoupon?.code})</span>
                    <span style={{ fontWeight: 600, color: "#16a34a" }}>-₹{appliedDiscount.toLocaleString()}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                  <span style={{ color: "#6b7280" }}>Shipping</span>
                  <span style={{ fontWeight: 600, color: "#16a34a" }}>Free</span>
                </div>
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  borderTop: "1.5px solid #f3f4f6", paddingTop: "12px", marginTop: "4px",
                }}>
                  <span style={{ fontSize: "16px", fontWeight: 700, color: "#1f2937" }}>Total Payable</span>
                  <span style={{ fontSize: "18px", fontWeight: 800, color: "#f97316" }}>₹{total.toLocaleString()}</span>
                </div>
              </div>

              {currentStep === 3 && (
               <button
  onClick={handlePlaceOrder}
  disabled={loading || isPaying || cartItems.length === 0}
  style={{
    width: "100%",
    marginTop: "20px",
    padding: "15px",
    borderRadius: "12px",
    background:
      loading || isPaying
        ? "#d1d5db"
        : "linear-gradient(135deg, #f97316, #ea580c)",
    color: "#fff",
    border: "none",
    cursor: loading || isPaying ? "not-allowed" : "pointer",
  }}
>
  {loading || isPaying ? "Processing..." : `Pay ₹${total.toLocaleString()}`}
</button>
              )}

              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: "6px", marginTop: "14px", color: "#9ca3af", fontSize: "12px",
              }}>
                🔒 Secure & Encrypted Payment
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;